import React, { FC, useEffect, useRef, useState } from 'react';
import {
  MonitoringFormCriteriaBlockObjectType,
  MonitoringFormCriteriaBulkType,
  MonitoringFormCriteriaObjectType,
} from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';
import useTranslation from '@app/hooks/use-translation';
import {
  useDeleteMFBlockMutation,
  useLazyGetMFBlockSingleQuery,
  useOrderMFCriteriaBlockMutation,
  useOrderMFCriteriaMutation,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { TableColumn, TableItem } from '@app/components/rubber-table/table.types';
import { Button, CircleButton, EasyModal, TooltipWrapper } from '@ui';
import AccordionItemUI from '@app/components/ui/Accordion/AccordionItem';
import { RemovalModal } from '@app/components/removal-modal';
import { ColsWithoutTable } from '@app/components/rubber-table/blocks/cols-without-table';

import { useOutletContext } from 'react-router-dom';

import { RubberTableWithGrid } from '@app/components/rubber-table-with-grid';

import Icon from '@app/components/ui/icons';

import { MonitoringFormSettingsOutletType } from '../../../monitoring-forms-settings.outlet';

import MFormBlockForm from './MForm-block-form';

export const CriteriaTable: FC<{
  criteriaListWithBlocks: MonitoringFormCriteriaBulkType | undefined;
  handleEditCriteria(id: string): void;
  handleDeleteCriteria(id: string): void;
}> = (props) => {
  const { monitoringFormData } = useOutletContext<MonitoringFormSettingsOutletType>();

  const { criteriaListWithBlocks, handleEditCriteria, handleDeleteCriteria } = props;
  const { t } = useTranslation(
    'pages.qmManage.monitoring_forms.createMonitoringForm.criteria_table',
  );

  const [mFBlockEditableData, setMFBlockEditableData] =
    useState<MonitoringFormCriteriaBlockObjectType>();
  const [removalBlockId, setRemovalBlockId] = useState<string>();
  const [loadingBlockId, setLoadingId] = useState<string>();

  // api
  const [getMFBlockInfo] = useLazyGetMFBlockSingleQuery();
  const [deleteMFBlock] = useDeleteMFBlockMutation();
  const [changeRowOrder] = useOrderMFCriteriaMutation();
  const [changeBlockOrder] = useOrderMFCriteriaBlockMutation();
  // api
  function onEditMFBlock(id: string) {
    getMFBlockInfo({ id })
      .unwrap()
      .then((blockInfo) => {
        setMFBlockEditableData(blockInfo);
        setCreateBlockField(true);
      });
  }
  function onDeleteMFBlock(id: string) {
    setRemovalBlockId(id);
  }
  function handleApproveDeleteBlock() {
    if (!removalBlockId) return;
    deleteMFBlock({ id: removalBlockId });
  }

  const [activeAccordionItem, setActiveAccordionItem] = React.useState<string[]>([]);
  const [createBlockField, setCreateBlockField] = React.useState(false);

  function handleAccordionClick({ id }: { id: string }) {
    const formattedAccordionArray = activeAccordionItem.includes(id)
      ? activeAccordionItem.filter((accordionItem) => accordionItem !== id)
      : [...activeAccordionItem, id];
    setActiveAccordionItem(formattedAccordionArray);
  }

  function onClose() {
    setCreateBlockField(false);
    setMFBlockEditableData(undefined);
  }

  function handleChangeTableRowOrders(orders: { order: number; id: string }[], cblock_id?: string) {
    const criteria =
      cblock_id === 'no_block'
        ? criteriaListWithBlocks?.criteria.plain
        : criteriaListWithBlocks?.criteria.blocks.find(({ id }) => cblock_id === id)?.criteria;
    try {
      for (let i = 0; i < orders.length; i++) {
        const oldOrder = criteria?.find((crit) => crit.id === orders[i].id)?.order;
        if (oldOrder === orders[i].order) continue;
        changeRowOrder({
          params: { cblock_id: cblock_id === 'no_block' ? undefined : cblock_id, id: orders[i].id },
          body: { order: orders[i].order },
        })
          .unwrap()
          .then(() => setLoadingId(cblock_id))
          .finally(() => setTimeout(() => setLoadingId(undefined), 700));
        return;
      }
    } catch {
      console.error('D&D error');
      setLoadingId(undefined);
    }
  }

  function getCriteriaTableData(data: MonitoringFormCriteriaObjectType[]): TableItem[] {
    return data.map((criteria) => ({
      name: criteria.name,
      dt_add: criteria.dt_add,
      id: criteria.id,
      type: t(`${criteria.type.toLowerCase()}`),
      comments_type: t(`${criteria.comments.type.toLowerCase()}`),
      rating_scale_name: criteria.settings.rating_scale.name || '',
      additionally: (
        <div className="flex items-center flex-wrap gap-[5px]">
          {typeof criteria?.crit_block === 'number' && (
            <div className="text-[11px] p-[2px_5px] rounded-[24px] border-basic_red border-[1px] w-fit">
              {t('crit_block')}
            </div>
          )}
          {typeof criteria?.crit_mform === 'number' && (
            <div className="text-[11px] p-[2px_5px] rounded-[24px] border-basic_red border-[1px] w-fit">
              {t('crit_mf')}
            </div>
          )}
          {criteria?.settings?.na_option && (
            <div className="text-[11px] p-[2px_5px] rounded-[24px] border-basic_red border-[1px] w-fit">
              {t('na_option')}
            </div>
          )}
        </div>
      ),
      actions: (
        <div className="w-full flex items-center justify-center gap-[5px] ">
          <CircleButton
            icon="SettingIcon"
            hintTitle={t('hints.setting')}
            onClick={() => handleEditCriteria(criteria.id)}
            size={18}
            className="hover:text-action"
          />
          <CircleButton
            icon="TrashIcon"
            hintTitle={t('hints.delete')}
            size={18}
            className="text-basic_red"
            disable={monitoringFormData?.status === 'ACTIVE'}
            onClick={() => handleDeleteCriteria(criteria.id)}
          />
        </div>
      ),
    }));
  }
  const criteriaTableColumns: TableColumn[] = [
    { index: 'dragDrop', title: '', maxWidth: 30 },
    { index: 'name', title: t('name') },
    { index: 'dt_add', title: t('dt_add') },
    { index: 'rating_scale_name', title: t('rating_scale_name') },
    { index: 'additionally', title: t('additionally') },
    { index: 'type', title: t('type') },
    { index: 'comments_type', title: t('comments_type') },
    { index: 'actions', title: t('actions'), maxWidth: 110 },
  ];
  //D&D
  const lastIndex = useRef<number | null>(null);
  const draggedIndex = useRef<number | null>(null);
  const [dragAndDrop, setDragAndDrop] = useState<{
    draggedTo: null | number;
    isDragging: boolean;
    updatedOrder: any;
  }>({
    draggedTo: null,
    isDragging: false,
    updatedOrder: [],
  });
  const [data, setData] = useState(criteriaListWithBlocks?.criteria.blocks);
  const [isUpdatingFromBackend, setIsUpdatingFromBackend] = useState(false);
  useEffect(() => {
    if (
      !dragAndDrop.isDragging &&
      !isUpdatingFromBackend &&
      criteriaListWithBlocks?.criteria.blocks?.length
    ) {
      setData(criteriaListWithBlocks.criteria.blocks);
    }
  }, [criteriaListWithBlocks?.criteria.blocks, dragAndDrop.isDragging, isUpdatingFromBackend]);

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    if (!dragEnabled.current) {
      event.preventDefault();
      return;
    }
    draggedIndex.current = index;
    setDragAndDrop((prev) => ({
      ...prev,
      draggedFrom: index,
      isDragging: true,
    }));

    event.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();

    if (draggedIndex.current === null) return;

    const from = draggedIndex.current;
    const to = index;

    if (from === to) return;

    if (lastIndex.current !== null && lastIndex.current === to) return;

    lastIndex.current = to;

    setData((prev) => {
      if (!prev) return prev;

      const updated = [...prev];
      const item = updated[from];

      updated.splice(from, 1);
      updated.splice(to, 0, item);

      return updated;
    });

    draggedIndex.current = to;
  };

  const onDrop = async () => {
    lastIndex.current = null;
    draggedIndex.current = null;
    setDragAndDrop({
      ...dragAndDrop,
      draggedTo: null,
      isDragging: false,
    });

    const updated =
      data?.map((item, index) => ({
        ...item,
        order: index,
      })) || [];

    setData(updated);

    try {
      setIsUpdatingFromBackend(true);

      const item = updated.find((item) => {
        const oldOrder = criteriaListWithBlocks?.criteria.blocks?.find(
          (b) => b.id === item.id,
        )?.order;

        return oldOrder !== item.order;
      });

      if (!item) return;

      await changeBlockOrder({
        params: { id: item.id },
        body: { order: item.order },
      }).unwrap();
    } catch {
      console.error('D&D error');
    } finally {
      setTimeout(() => setIsUpdatingFromBackend(false), 500);
    }
  };

  const dragEnabled = useRef(false);
  const onMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    dragEnabled.current = !!target.closest('.draggable-container');
  };
  const dragEventMArkup = (
    <div
      onMouseDown={onMouseDown}
      className="ml-[-7px]  cursor-pointer z-10 absolute left-[10px] draggable-container top-[50%] translate-x-1/2 -translate-y-1/2"
    >
      <Icon size={7} name="GridIcon" className="text-3color" />
    </div>
  );
  //D&D

  return (
    <>
      <div className="flex flex-col">
        {(!!criteriaListWithBlocks?.criteria.blocks.length ||
          !!criteriaListWithBlocks?.criteria.plain.length) && (
          <ColsWithoutTable columns={criteriaTableColumns} />
        )}

        {data?.map(({ id, name, criteria_count, criteria }, index) => {
          const isOpened = activeAccordionItem.includes(id);
          return (
            <div
              key={id}
              draggable={!isOpened}
              onDragStart={(e) => (isOpened ? undefined : handleDragStart(e, index))}
              onDragEnd={isOpened ? undefined : onDrop}
              onDragEnter={(event) => (isOpened ? undefined : handleDragOver(event, index))}
              className="relative z-[1]"
            >
              {!isOpened && dragEventMArkup}
              <AccordionItemUI
                variant="filled"
                title={
                  <CriteriaAccordionTitle
                    formId={id}
                    title={name}
                    count={criteria_count}
                    onEditBlock={onEditMFBlock}
                    onDeleteBlock={onDeleteMFBlock}
                    isOpened={isOpened}
                  />
                }
                id={id}
                active={isOpened}
                clickBtnChild={handleAccordionClick}
              >
                <RubberTableWithGrid
                  isPending={loadingBlockId === id}
                  onChangeOrder={(orders) => handleChangeTableRowOrders(orders, id)}
                  withoutCols
                  block_id={id}
                  columns={criteriaTableColumns}
                  dataSource={getCriteriaTableData(criteria)}
                />
              </AccordionItemUI>
            </div>
          );
        })}
        <div className="pt-[20px]">
          <Button
            icon="CirclePlusIcon"
            label={t('new_block')}
            onClick={() => setCreateBlockField((prev) => !prev)}
            disabled={monitoringFormData?.status !== 'NEW'}
          />
        </div>
        <div className="py-[20px]">
          <RubberTableWithGrid
            withoutCols
            isPending={loadingBlockId === 'no_block'}
            block_id={'no_block'}
            onChangeOrder={(orders) => handleChangeTableRowOrders(orders, 'no_block')}
            columns={criteriaTableColumns}
            dataSource={getCriteriaTableData(criteriaListWithBlocks?.criteria?.plain || [])}
          />
        </div>
      </div>
      <EasyModal
        withoutFooter
        label={t('create_block_modal.label')}
        variant="mediumW650"
        onClose={onClose}
        show={createBlockField}
      >
        {createBlockField && (
          <MFormBlockForm mFBlockEditableData={mFBlockEditableData} onClose={onClose} />
        )}
      </EasyModal>
      <RemovalModal
        onClose={() => setRemovalBlockId(undefined)}
        show={!!removalBlockId}
        handleDelete={handleApproveDeleteBlock}
        description={t('delete_block_description')}
      />
    </>
  );
};

type CriteriaAccordionTitlePropsType = {
  title: string;
  formId: string;
  isOpened: boolean;
  count: number;
  onEditBlock(id: string): void;
  onDeleteBlock(id: string): void;
};
const CriteriaAccordionTitle: FC<CriteriaAccordionTitlePropsType> = (props) => {
  const { title, formId, isOpened, count, onEditBlock, onDeleteBlock } = props;
  const { t } = useTranslation(
    'pages.qmManage.monitoring_forms.createMonitoringForm.criteria_table',
  );
  function editGroup() {
    onEditBlock(formId);
  }

  function onDelete() {
    onDeleteBlock(formId);
  }

  return (
    <>
      <div className="flex items-center gap-[15px] ">
        <span className="font-[700] text-[16px] text-2color leading-[110%] tracking-tight">
          {title}
        </span>
        {!isOpened && (
          <div className="flex justify-center items-center text-xs w-[17px] h-[17px] rounded-full  text-[11px] bg-defaultBgBtn !text-white">
            {count}
          </div>
        )}
        {isOpened && (
          <div className=" flex items-center gap-[10px] ">
            <TooltipWrapper content={t('hints.edit')} id={t('hints.edit')}>
              <CircleButton
                size={14}
                onClick={editGroup}
                className={'text-action'}
                // className={`${presetsCount === 0 ? 'text-dangerBg' : 'text-3color'}`}
                icon={'EditIcon'}
                stopPropagation
              />
            </TooltipWrapper>
            <TooltipWrapper content={t('hints.delete')} id={t('hints.delete')}>
              <CircleButton
                size={14}
                onClick={onDelete}
                className={'text-dangerBg'}
                // className={`${presetsCount === 0 ? 'text-dangerBg' : 'text-3color'}`}
                icon={'TrashIcon'}
                stopPropagation
              />
            </TooltipWrapper>
          </div>
        )}
      </div>
    </>
  );
};
